import type HttpMethods from "../enums/HttpMethods";
import AuthenticationService from "./AuthenticationService";
import DeprecatedApiService from "./DeprecatedApiService";

interface IProductRansackParam {
    attribute: string;
    predicate: string;
    value: string;
}

export class DeprecatedAResourceService {
    protected getPagingUri(
        uri: string,
        perPage?: number,
        page?: number,
        ransackParams?: IProductRansackParam[],
    ): string {
        const params = [];
        // params = [...params, ...urlParams];
        if (perPage) {
            params.push(`per_page=${perPage}`);
        }
        if (page) {
            params.push(`page=${page}`);
        }
        if (ransackParams) {
            const query = ransackParams.map((param) => {
                const key = `q[${param.attribute}_${param.predicate}]`;
                return `${encodeURIComponent(key)}=${encodeURIComponent(param.value)}`;
            });
            params.push(...query);
        }
        if (params.length) {
            return `${uri}${uri.includes("?") ? "&" : "?"}${params.join("&")}`;
        }
        return uri;
    }

    protected async sentData<T = unknown>(
        baseUri: string,
        url: string,
        method: HttpMethods,
        data: unknown,
        transformToSnakeCase = true,
    ) {
        const apiService: DeprecatedApiService = new DeprecatedApiService(baseUri);
        return (await apiService.send<T>(url, method, data, this.getHeaders(), transformToSnakeCase)) as T;
    }

    protected async getObjectData<T, D = unknown>(
        baseUri: string,
        uri: string,
        method: HttpMethods,
        data?: unknown,
        perPage?: number,
        page?: number,
        ransackParams?: IProductRansackParam[],
    ): Promise<T> {
        const apiService: DeprecatedApiService = new DeprecatedApiService(baseUri);
        return (await apiService.send<T>(
            this.getPagingUri(uri, perPage, page, ransackParams),
            method,
            data,
            this.getHeaders(),
        )) as T;
    }

    protected async getArrayData<T, D = unknown>(
        baseUri: string,
        uri: string,
        method: HttpMethods,
        data?: D,
        perPage?: number,
        page?: number,
    ): Promise<T[]> {
        return (await this.getData<T[], D>(baseUri, uri, method, data, perPage, page)) ?? [];
    }

    private async getData<T, D = unknown>(
        baseUri: string,
        uri: string,
        method: HttpMethods,
        data?: D,
        perPage?: number,
        page?: number,
    ): Promise<T | undefined> {
        const apiService: DeprecatedApiService = new DeprecatedApiService(baseUri);
        return (await apiService.send<T>(this.getPagingUri(uri, perPage, page), method, data, this.getHeaders())) as T;
    }

    protected getHeaders(): Record<string, string> {
        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${AuthenticationService.token}`,
            // Add more headers as needed
        };
    }
}
